import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { UsersService } from '@core/services/users.service';
import {
  consoleLocaleHref,
  normalizeConsoleUiLang,
  type ConsoleUiLang,
} from '@shared/functions/console-locale-href.function';
import { EMPTY, Observable, map, tap } from 'rxjs';

/**
 * Console UI Language switch adapter: when authenticated, persist User Language
 * on Platform then navigate into the matching locale; when unauthenticated,
 * navigate only (no Platform write).
 */
@Injectable({
  providedIn: 'root',
})
export class UiLanguageSwitchService {
  private readonly authService = inject(AuthService);

  private readonly usersService = inject(UsersService);

  /** Angular sourceLocale is `fr`; LOCALE_ID is always set in localized builds. */
  private readonly loadedLang =
    normalizeConsoleUiLang(inject(LOCALE_ID)) ?? 'fr';

  /**
   * Completes after a successful Platform persist (when needed).
   * Errors if authenticated persist fails — callers must not assume the UI
   * language changed. Navigation (when required) runs only after persist.
   */
  switchTo(requested: ConsoleUiLang): Observable<void> {
    const lang = normalizeConsoleUiLang(requested);
    if (!lang) {
      return EMPTY;
    }

    const needsNavigation = lang !== this.loadedLang;
    const href = consoleLocaleHref(lang, window.location.pathname);
    const navigate = () => {
      if (needsNavigation) {
        window.location.href = href;
      }
    };
    const user = this.authService.user$.getValue();

    if (!user?.id) {
      navigate();
      return EMPTY;
    }

    if (user.lang === lang) {
      navigate();
      return EMPTY;
    }

    return this.usersService.update(user.id, { lang }).pipe(
      tap((updated) => {
        this.authService.setUser({
          user: { ...user, ...updated, lang },
        });
        navigate();
      }),
      map(() => undefined)
    );
  }
}
